import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import {
  SquaresFour, UsersThree, Receipt, IdentificationCard, UserSwitch,
  Calculator, ClipboardText, ChartBar, SignOut, Wrench, Globe, Stack, Briefcase, FileText, Star, Gear, ShieldCheck, ChartLineUp, MapPin, Buildings, DeviceMobile, CurrencyInr, ChatCircleDots, Images,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: SquaresFour, module: "dashboard", roles: ["admin", "finance", "ops"], end: true },
  { to: "/partners", label: "Partners", icon: UsersThree, module: "partners", roles: ["admin", "ops"] },
  { to: "/applications", label: "Applications", icon: IdentificationCard, module: "applications", roles: ["admin", "ops"] },
  { to: "/leads", label: "Leads", icon: ChatCircleDots, module: "leads", roles: ["admin", "ops", "finance"] },
  { to: "/jobs", label: "Jobs", icon: Briefcase, module: "jobs", roles: ["admin", "ops", "finance"] },
  { to: "/collect-payment", label: "Collect Payment", icon: CurrencyInr, module: "collect_payment", roles: ["admin", "ops", "finance"] },
  { to: "/estimate-calculator", label: "Estimate Calculator", icon: Calculator, module: "estimate", roles: ["admin", "ops"] },
  { to: "/catalog", label: "Service Catalog", icon: Stack, module: "catalog", roles: ["admin"] },
  { to: "/sops", label: "Service SOPs", icon: ClipboardText, module: "catalog", roles: ["admin", "ops"] },
  { to: "/ratecards", label: "Ratecards", icon: Receipt, module: "ratecards", roles: ["admin", "ops"] },
  { to: "/documents", label: "Documents", icon: FileText, module: "documents", roles: ["admin", "ops"] },
  { to: "/feedback", label: "Feedback", icon: Star, module: "feedback", roles: ["admin", "ops", "finance"] },
  { to: "/website", label: "Website Control", icon: Globe, module: "website", roles: ["admin"] },
  { to: "/customers", label: "Customers", icon: IdentificationCard, module: "customers", roles: ["admin", "ops", "finance"] },
  { to: "/competition", label: "Competition", icon: ChartLineUp, module: "competition", roles: ["admin", "ops"] },
  { to: "/serviceability", label: "Serviceability", icon: MapPin, module: "serviceability", roles: ["admin", "ops"] },
  { to: "/cities", label: "Cities", icon: Buildings, module: "cities", roles: ["admin", "ops"] },
  { to: "/sell-devices", label: "Sell Devices", icon: DeviceMobile, module: "sell_orders", roles: ["admin", "ops"] },
  { to: "/media-requirements", label: "Media Requirements", icon: Images, module: "media_config", roles: ["admin", "ops"] },
  { to: "/accounting", label: "Accounting", icon: Calculator, module: "accounting", roles: ["admin", "finance"] },
  { to: "/reports", label: "Reports", icon: ChartBar, module: "reports", roles: ["admin", "finance"] },
  { to: "/audit", label: "Audit Logs", icon: ClipboardText, module: "audit", roles: ["admin", "finance"] },
  { to: "/permissions", label: "Roles & Access", icon: ShieldCheck, module: "permissions", roles: ["admin"] },
  { to: "/users", label: "Team / Users", icon: UserSwitch, module: "users", roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Gear, module: "settings", roles: ["admin"] },
];

export function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    api.get("/permissions/me").then((r) => setAllowed(r.data.modules)).catch(() => setAllowed(null));
  }, []);

  const items = NAV.filter((n) => n.roles.includes(user?.role) && (allowed === null || allowed.includes(n.module)));

  return (
    <div className="flex min-h-screen bg-background">
      <aside className="w-64 shrink-0 border-r border-border bg-card flex flex-col fixed h-screen" data-testid="sidebar">
        <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
          <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
            <Wrench size={18} weight="bold" className="text-primary-foreground" />
          </div>
          <span className="font-heading font-bold text-lg tracking-tight">Fixigo</span>
          <span className="label-caps text-[9px] mt-1">Admin</span>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`nav-${n.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <n.icon size={18} weight="bold" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="px-3 py-2 mb-1">
            <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide">{user?.role}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-sm text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors duration-200"
          >
            <SignOut size={18} weight="bold" />
            Sign out
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 min-w-0">{children}</main>
    </div>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="h-16 border-b border-border bg-card px-8 flex items-center justify-between sticky top-0 z-10">
      <div>
        <h1 className="font-heading text-xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{actions}</div>
    </div>
  );
}
